/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import 'cross-fetch/polyfill';
import React from 'react';
import { useSelector } from 'react-redux';
import { object, node } from 'prop-types';
import mbpLogger from 'mbp-logger';

import { gql } from '@apollo/client';
import { Query } from '@apollo/client/react/components';

import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';

import brandThemes from '../../../../components/AppShell/brandtheme';
// helpers
import { GRAPHQL_ENV } from '../../../../gql';
import { buildTypographyTheme } from './BrandThemeBuilder';
// redux
import styleOverrides from '../../../../components/AppShell/MuiStyleOverrides';
import { getFeatureFlag } from '../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';

const BrandTheme = ({ brand, children }) => {
    const checkoutVariation = useSelector(getFeatureFlag('which-checkout-variation')) || false;
    if (brand && brand['domain-name']) {
        const THEME_QUERY = gql`
                query brand_configuration {
                    findContent(brand: "${brand['domain-name']}", environment: "${GRAPHQL_ENV}", contentType:"brand_configuration")
                    {
                        content
                    }
                }
            `;

        /**
             *
             * addCMSTheme - add Contentstack theme values to brandTheme
             *
             * By convention, all CMS values are snake_case, rather than standard js camelCase.
             * It was decided to preserve the snake_case when transferring values to brandTheme,
             * so that brandTheme attributes would always exactly match CMS names.
             * It also has the effect of signaling an attribute came from CMS rather than a hardcoded/legacy value.
             *
             * @param {object} brandTheme brand's theme object from brandsConfig[ brandcode ], e.g. brandsConfig.HD
             * @param {object} cmsData    brand theme values from contentstack (object in first entry of content.entries of brand_theme_full)
             */
        const addCMSTheme = (brandTheme, cmsData) => {
            // font-family to use for this brand's site
            // e.g. "font-family: lato, Arial, Verdana, sans-serif;"
            if (cmsData.global_font_family) {
                brandTheme.global_font_family = cmsData.global_font_family;      // eslint-disable-line no-param-reassign
            } else {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    query: THEME_QUERY,
                    component: 'AppShell.js',
                    message: 'Brand has no global_font_family',
                    env: GRAPHQL_ENV,
                });
            }

            brandTheme.palette.cms = brandTheme.palette.cms ? brandTheme.palette.cms : {};          // eslint-disable-line no-param-reassign

            // Color palette from CMS.
            // e.g.
            //     brandTheme.palette.cms.primary = "a1001a"
            //     brandTheme.palette.cms['Body Font'] = "#2f2f2f"
            ['colors', 'cta_colors', 'layout_colors', 'additional_colors', 'additional_palette_colors'].forEach((sectionName) => {
                if (cmsData[sectionName]) {
                    const section = cmsData[sectionName];
                    if (Array.isArray(section)) {
                        /*
                                Entries expected in this form:
                                    {
                                        "title": "Body Font",
                                        "color": {
                                            "color": "#2f2f2f",
                                            "description": "NIGHT RIDER",
                                            "transparency": "",
                                            "version": "1"
                                        }
                                    },
                            */
                        section.forEach((entry) => {
                            brandTheme.palette.cms[entry.title] = entry.color.color;      // eslint-disable-line no-param-reassign
                        });
                    } else {
                        /*
                                Entries expected in this form:
                                    "primary_hover": {
                                        "color": "#790000",
                                        "description": "MAROON",
                                        "transparency": "",
                                        "version": "1"
                                    },
                           */
                        Object.keys(section).forEach((entryKey) => {
                            brandTheme.palette.cms[entryKey] = section[entryKey].color;      // eslint-disable-line no-param-reassign
                        });
                    }
                }
            });

            // e.g. brandTheme.logos.primary_logo = {
            //     "content_type": "image/svg+xml",
            //     "filename": "hd-logo-black.svg",
            //     "url": "https://images.contentstack.io/v3/assets/blt89dbf1c763ec00a6/bltff290eac2ecfd735/5e20dfa82d54964f9f397316/hd-logo-black.svg",
            //     "title": "hd-logo-black.svg",
            //     "description": "Harry and David Logo"
            // }
            if (cmsData.logos) {
                brandTheme.logos = {};      // eslint-disable-line no-param-reassign
                Object.keys(cmsData.logos).forEach((logoKey) => {
                    if (cmsData.logos[logoKey]) {
                        const cmsLogo = cmsData.logos[logoKey];
                        brandTheme.logos[logoKey] = {      // eslint-disable-line no-param-reassign
                            contentType: cmsLogo.content_type || null,
                            filename: cmsLogo.filename || null,
                            description: cmsLogo.description || null,
                            title: cmsLogo.title || null,
                            url: cmsLogo.url || null,
                        };
                    }
                });
            }

            // Array of fonts, as defined by business in Contentstack (Brand Configuration > Typography Stack),
            // in order of primacy e.g. primary font is:
            //    brandTheme.cms.typography[0] = {
            //     "font_name": "Lato - sans",
            //     "font_style": "Main copy, sans serif",
            //     "font_stack": "font-family: 'latoregular', Arial, Verdana, sans-serif;",
            //     "font_file_primary": "DancingScript-Regular.woff2",
            //     "font_file_fallback": "DancingScript-Regular.woff"
            // }
            // Generates <style> object to inject into #root
            if (cmsData.typography || cmsData.global_font_family) {
                // eslint-disable-next-line no-param-reassign
                brandTheme.typography = buildTypographyTheme(cmsData, brandTheme.typography);
            }

            // Other key-pair values from Contentstack,
            // such as cta_icon = ">" or ">>", presentation_family = 'food' or 'flower'
            // e.g. if (brandTheme.presentation_family === 'food') {
            if (cmsData.other) {
                Object.assign(brandTheme, cmsData.other);
            }
        };

        // TODO
        // - implement a better way to get brand instead of props drilling -> Redux store -> Connect to parent components with queries, hand to children as props?
        return (
            <Query query={THEME_QUERY}>
                {({ loading, error, data }) => {
                    if (loading) {
                        return null;
                    }

                    if (error) {
                        mbpLogger.logError({
                            appName: process.env.npm_package_name,
                            query: THEME_QUERY,
                            component: 'AppShell.js',
                            message: 'Error loading theme from Graphql',
                            env: GRAPHQL_ENV,
                            error,
                        });
                        // DO NOT ABORT -- continue onward if THEME not in contentstack
                    }

                    // brandThemes[brand.code] may not yet exist for brands under development.
                    // Must contain valid brand configuration data for production brands.

                    if (brandThemes[brand.code]) {
                        if (!data || !data.findContent || !data.findContent.content || !data.findContent.content.entries
                            || !Array.isArray(data.findContent.content.entries)
                            || !data.findContent.content.entries[0]) {
                            mbpLogger.logWarning({
                                appName: process.env.npm_package_name,
                                query: THEME_QUERY,
                                component: 'AppShell.js',
                                message: 'No theme data returned for query',
                                env: GRAPHQL_ENV,
                            });
                        } else {
                            addCMSTheme(brandThemes[brand.code], data.findContent.content.entries[0]);
                        }
                    }

                    const {
                        palette,
                        typography,
                        spacing,
                        breakpoints,
                    } = brandThemes[brandThemes[brand.code] ? brand.code : '18F'];  // if brand not set up yet, use 18F settings
                    let { overrides } = brandThemes[brandThemes[brand.code] ? brand.code : '18F'];

                    let finalBreakPoints = breakpoints;

                    if (['enterprise2', 'enterprise'].includes(checkoutVariation)) {
                        if (!finalBreakPoints?.values) {
                            const defaultTheme = createTheme();
                            finalBreakPoints = {
                                values: {
                                    ...defaultTheme.breakpoints?.values,
                                    sm: 700,
                                    md: 998,
                                },
                            };
                        }
                    }
                    // To get font family from cms
                    typography.getFontFamily = (fontName) => {
                        const filteredFont = (typography?.cms || []).filter((font) => font.font_name === fontName);
                        if (filteredFont.length) {
                            return filteredFont[0].font_style;
                        }
                        return null;
                    };

                    overrides = { ...overrides, ...styleOverrides(palette) };

                    const theme = createTheme({
                        palette,
                        typography,
                        spacing,
                        breakpoints: finalBreakPoints,
                        overrides,
                    });
                    const innerHeightScreen = 'calc(110vh - 236px)';
                    return (
                        <>
                            <ThemeProvider theme={theme}>
                                <>
                                    <CssBaseline />
                                    {typography.styleblock}
                                    <div style={{ minHeight: `${innerHeightScreen}` }}>
                                        {children}
                                    </div>
                                </>
                            </ThemeProvider>
                        </>
                    );
                }}
            </Query>
        );
    }

    return null;
};

BrandTheme.propTypes = {
    brand: object.isRequired,
    children: node.isRequired,
};

BrandTheme.defaultProps = {};

export default BrandTheme;
